import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Container, Box, Typography, CircularProgress, Alert, Divider } from '@mui/material';
import { Review } from './components/Review/Review';
import { theme } from './theme/theme';
import store, { useAppDispatch, useTypedSelector } from './store';
import { initializeFromToken } from './store/slices/gbpSlice';
import { getTokenFromUrl, isValidToken } from './utils/urlUtils';
import { InitClarity } from './clarity';
import { ClarityRouteTracker } from './common/components/ClarityRouteTracker';

// Initialize Microsoft Clarity at module level
const shouldInitClarity = ['production', 'release', 'staging'].includes(
  process.env.REACT_APP_BUILD_ENV || ''
);

if (shouldInitClarity) {
  InitClarity();
}

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { actionToken, reviews } = useTypedSelector((state) => state.gbpSlice);
  const [initializing, setInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Get token from URL
        const token = getTokenFromUrl();
        
        if (!isValidToken(token)) {
          setInitError('Your session has expired. To manage this review, please go to your dashboard.');
          setInitializing(false);
          return;
        }

        // Initialize from token (this will get action token and fetch review)
        await dispatch(initializeFromToken(token)).unwrap();
        
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setInitError(
          error instanceof Error 
            ? error.message 
            : 'Failed to load review. Please check your link and try again.'
        );
      } finally {
        setInitializing(false);
      }
    };

    initializeApp();
  }, [dispatch]);

  // Handle mobile keyboard dismiss - reset scroll position
  useEffect(() => {
    const handleFocusOut = () => {
      // Small delay to ensure keyboard is fully dismissed
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    };

    // Listen for blur events on input/textarea elements
    const handleBlur = (e: FocusEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        handleFocusOut();
      }
    };

    // Add event listener
    document.addEventListener('focusout', handleBlur as EventListener);

    // Cleanup
    return () => {
      document.removeEventListener('focusout', handleBlur as EventListener);
    };
  }, []);

  // Show loading state
  if (initializing || actionToken.loading || reviews.loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading review...
        </Typography>
      </Container>
    );
  }

  // Show error state
  if (initError || actionToken.error || reviews.error) {
    const errorMessage = initError || actionToken.error || reviews.error;
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Unable to Load Review
          </Typography>
          <Typography variant="body2">
            {errorMessage}
          </Typography>
        </Alert>
      </Container>
    );
  }

  // Show review if we have all required data
  if (!actionToken.data || !reviews.currentReview) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            No Review Found
          </Typography>
          <Typography variant="body2">
            Unable to find review data. Please check your link.
          </Typography>
        </Alert>
      </Container>
    );
  }

  // Convert data to ReviewProps format
  const reviewProps = {
    profileName: reviews.currentReview.reviewerDisplayName,
    timestamp: reviews.currentReview.createTime,
    rating: reviews.currentReview.starRating,
    reviewText: reviews.currentReview.comment,
    profilePhotoUrl: reviews.currentReview.reviewerProfilePhotoUrl,
    reply: reviews.currentReview.replyComment ? {
      businessName: 'Business Owner',
      text: reviews.currentReview.replyComment,
      timestamp: reviews.currentReview.replyUpdateTime || '',
    } : undefined,
    reviewId: reviews.currentReview.reviewId,
    accountId: actionToken.data.accountId,
    submittingReviewId: reviews.submittingReviewId,
    invitationId: (reviews.currentReview as any).invitationId,
  };

  return (
    <Switch>
      <Route path="/" exact>
        <div>
          <Box sx={{ 
            backgroundColor: 'background.paper', 
            borderRadius: 2, 
            p: { xs: 1.5, sm: 2 },
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  textAlign: 'center', 
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  fontFamily:'system-ui'
                }}
              >
                Reply to Reviews & Ask for a Video
              </Typography>
              <Divider />
              <Review {...reviewProps} />
            </Box>
            {/* <Box sx={{ 
              textAlign: 'center', 
              color: 'text.secondary',
              fontSize: '0.75rem',
              pb: 0
            }}>
              powered by{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                SocialV
              </Box>
            </Box> */}
          </Box>
        </div>
      </Route>
      {/* Add additional routes here as needed */}
    </Switch>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ 
          minHeight: '100vh', 
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Router>
            <ClarityRouteTracker />
            <AppContent />
          </Router>
        </Box>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
